import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import { debounce } from "lodash"
import moment from "moment-timezone";
import store from "@/store";
import ability from "@/libs/acl/ability";
import useFilterCounter from '@/components/Filters/useFiltersCount'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default function useList(showFilters = true) {

  const refListTable = ref(null);
  const toast = useToast();

  store.dispatch("unit/getAll");
  const unitOptions = computed(() => store.getters['unit/getSelectedItemsBrigades'])
  const unitFilter = ref(null);

  const remsFilter = ref(null);
  const remsOptions = computed(() =>
    unitOptions.value
      .filter(u => u.rems)
      .map(u => ({
        label: u.rems,
        value: u.value
      }))
  )

  const sirhFilter = ref(null);
  const sirhOptions = computed(() =>
    unitOptions.value
      .filter(u => u.sirh)
      .map(u => ({
        label: u.sirh,
        value: u.value
      }))
  )

  const categoryFilter = ref(null);
  const categoryOptions = computed(() =>
    unitOptions.value
      .filter(u => u.category)
      .map(u => ({
        label: u.category,
        value: u.value
      }))
  )

  const numberFloorsFilter = ref(null);
  /*const numberFloorsOptions = computed(() =>
    unitOptions.value
      .filter(u => u.floor_number)
      .map(u => ({
        label: u.floor_number,
        value: u.value
      }))
  )*/
  const numberFloorsOptions = computed(() => {
    const map = new Map()

    unitOptions.value.forEach(u => {
      if (u.floor_number && !map.has(u.floor_number)) {
        map.set(u.floor_number, {
          label: u.floor_number,
          value: u.floor_number
        })
      }
    })

    return Array.from(map.values())
  })

  const statusOptions = ([
    { value: 'complete', label: 'Completa'},
    { value: 'incomplete', label: 'Incompleta'}
  ]);
  const statusFilter = ref(null)

  const createdAtFilter = ref(null)
  const updatedAtFilter = ref(null)

  store.dispatch('sector/getAll')
  const sectorOptions = computed(() => store.getters['sector/getSelectedItems'] || [])
  const sectorFilter = ref(null)

  store.dispatch("brigades/getBrigadeUsers");
  const brigadeUsersOptions = computed(() => store.getters["brigades/getSelectedBrigadeUsersItems"] || []);
  const brigadeUsersFilter = ref(null);
  const brigadeUsersWithoutBrigadeOptions = computed(() => store.getters["brigades/getBrigadeUsersItems"] || []);

  store.dispatch("brigades/getBrigadeRoles");
  const brigadeRolesOptions = computed(() => store.getters["brigades/getSelectedBrigadeRolesItems"] || []);
  const brigadeRolesFilter = ref(null);

  store.dispatch("brigades/getBrigadeStatus");
  const brigadeStatusOptions = computed(() => store.getters["brigades/getSelectedBrigadeRolesItems"] || []);
  const brigadeStatusFilter = ref(null);

  const tableColumns = [
    { label: "unit.title", key: "unit.description", sortable: true },
    { label: "unit.rems", key: "unit.rems", sortable: true },
    { label: "unit.sirh", key: "unit.sirh", sortable: true },
    { label: "brigades.division", key: "unit.sector.name", sortable: true },
    { label: "brigades.type_category", key: "unit.category.name", sortable: true },
    { label: "brigades.number_floors", key: "unit.floors", formatter: (floors) => floors?.length ?? 0 },
    { label: "brigades.status", key: "status" },
    {
      label: "date.created_at",
      key: "created_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
    {
      label: "date.updated_at",
      key: "updated_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
  ];

  if (showFilters && (ability.can("update", "Brigade") || ability.can("delete", "Brigade"))) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);
  const action = ref("list");

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const fetchList = (ctx, callback) => {
    store
      .dispatch("brigades/getAll", {
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        action: action.value,
        unit: unitFilter.value,
        sector: sectorFilter.value,
        rems: remsFilter.value,
        sirh: sirhFilter.value,
        category: categoryFilter.value,
        numberFloors: numberFloorsFilter.value,
        status: statusFilter.value,
        createdAt: createdAtFilter.value,
        updatedAt: updatedAtFilter.value,
        user: brigadeUsersFilter.value,
      })
      .then((response) => {
        if (action.value == "export") {
          action.value = "list";
        } else {
          callback(response.data);
          totalList.value = response.meta.total;
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      unit: unitFilter.value,
      sector: sectorFilter.value,
      rems: remsFilter.value,
      sirh: sirhFilter.value,
      category: categoryFilter.value,
      numberFloors: numberFloorsFilter.value,
      status: statusFilter.value,
      createdAt: createdAtFilter.value,
      updatedAt: updatedAtFilter.value,
      user: brigadeUsersFilter.value,

    };
    refetchData();
  }
  const clearSearchFilters = () => {
    unitFilter.value = null;
    sectorFilter.value = null;
    remsFilter.value = null;
    sirhFilter.value = null;
    categoryFilter.value = null;
    numberFloorsFilter.value = null;
    statusFilter.value = null;
    createdAtFilter.value = null;
    updatedAtFilter.value = null;
    brigadeUsersFilter.value = null;
    buildFilters();
  }

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    refetchData,
    action,
    unitOptions,
    unitFilter,
    sectorOptions,
    sectorFilter,
    remsOptions,
    remsFilter,
    sirhOptions,
    sirhFilter,
    categoryFilter,
    categoryOptions,
    numberFloorsFilter,
    numberFloorsOptions,
    statusFilter,
    statusOptions,
    createdAtFilter,
    updatedAtFilter,
    brigadeUsersOptions,
    brigadeUsersFilter,
    brigadeRolesOptions,
    brigadeRolesFilter,
    brigadeStatusOptions,
    brigadeStatusFilter,
    filtersCount,
    brigadeUsersWithoutBrigadeOptions,
    buildFilters,
    clearSearchFilters,
  };
}
